import type { Dictionary } from "./it";

export const en: Dictionary = {
  common: {
    appName: "Eligo",
    next: "Next",
    back: "Back",
    cancel: "Cancel",
    confirm: "Confirm",
    save: "Save",
    close: "Close",
    yes: "Yes",
    no: "No",
    required: "Required field",
    select: "Please select",
    loading: "Loading...",
    error: "Error",
    eligible: "Eligible",
    notEligible: "Not eligible",
    inProgress: "In progress",
    info: "Info",
    skipToContent: "Skip to content",
    open: "Open",
    theme: "Theme",
    language: "Language",
    of: "of",
  },
  nav: {
    home: "Home",
    new: "New",
    trials: "Trials",
    saved: "Sent",
    assistant: "Chat",
  },
  assistant: {
    title: "Protocols assistant",
    subtitle: "Ask questions about the uploaded study protocols.",
    documents: "Documents",
    uploadCta: "Upload protocol PDFs",
    dropHint: "Drag here or tap to select (PDF)",
    askPlaceholder: "Ask a question about the protocols...",
    send: "Send",
    thinking: "Reading the protocols...",
    noProtocols: "Upload at least one PDF to get started.",
    emptyReady: "Ask a question: I'll answer based on the uploaded protocols.",
    dontKnow: "I can't find this information in the uploaded protocols.",
    sources: "Sources",
    errorGeneric: "Error contacting the assistant",
    statusExtracting: "Extracting...",
    statusIndexing: "Indexing...",
    statusReady: "Ready",
    statusError: "Error",
    removeDoc: "Remove document",
    clearChat: "Clear conversation",
    loadedProtocols: "Protocols",
    kbEmpty: "No protocols available yet.",
    knowsProtocols: "Knows the loaded protocols",
  },
  kbAdmin: {
    title: "Manage protocols",
    subtitle: "Upload the PDFs, build the knowledge base and publish it.",
    export: "Export knowledge base",
    step1: "Upload the protocol PDFs above and wait for the 'Ready' status.",
    step2: "Click 'Export knowledge base': it downloads the protocols-index.json file.",
    step3: "On GitHub, upload that file into the public/ folder (overwriting) and commit: the site updates on its own.",
  },
  landing: {
    tagline: "Patient assignment to clinical trials - acute, hemorrhagic and post-acute.",
    heroTitle: "Assign a patient\nto a trial.",
    newPatient: "New patient",
    newPatientDesc: "Check eligibility across all trials",
    assistantHeroTitle: "Your trial knowledge,\ninstantly.",
    askCta: "Ask about the protocols...",
    suggestions: ["Active trials", "Inclusion criteria", "Time windows"],
    inProgressTitle: "Patient in progress",
    pastPatients: "Past patients",
    inProgressSubtitle: "Resumed from localStorage",
    discard: "Discard",
    resume: "Resume",
    catalogTitle: "Trials catalog",
    catalogSubtitle: "Browse criteria for all active trials",
    catalogDesc: "Reference-only browse, no patient required.",
    patientId: "ID",
    age: "Age",
    nihss: "NIHSS",
    ltsw: "LTSW",
  },
  workflow: {
    title: "What would you like to do?",
    subtitle: "Select a workflow to start.",
    eligibilityTitle: "Eligibility evaluation",
    eligibilityDesc: "Guided history + imaging to identify suitable trials.",
    trialsTitle: "Trials info",
    trialsDesc: "Catalog with inclusion/exclusion criteria.",
    quickTitle: "Quick patient",
    quickDesc: "Fast entry for a patient already enrolled.",
  },
  trials: {
    title: "Clinical trials",
    countSuffix: "total",
    searchLabel: "Search",
    searchPlaceholder: "name, category, intervention...",
    filterAll: "All",
    filterIschemic: "Ischemic",
    filterHemorrhagic: "Hemorrhagic",
    filterPostAcute: "Post-acute",
    empty: "No trial matches the filters.",
    window: "Window",
    backToCatalog: "Catalog",
    keyCriteria: "Key criteria",
    imaging: "Imaging",
    intervention: "Intervention",
    treatment: "Treatment",
    notes: "Notes",
    visits: "Follow-up visits",
    openRandomizer: "Open randomizer",
    fieldWindow: "Window",
    fieldAge: "Age",
    fieldMrs: "pre-mRS",
    fieldNihss: "NIHSS",
    fieldAspects: "ASPECTS",
    thrombolytic: "Thrombolytic",
    thrombectomy: "Thrombectomy",
    preEvtLabs: "Pre-EVT labs",
    consent: "Consent",
    notFoundTitle: "Trial not found",
    notFoundDesc: "is not in the catalog.",
    backCatalog: "Back to catalog",
  },
  preImaging: {
    title: "Pre-Imaging",
    subtitle: "Initial history for WeTrust evaluation.",
    identification: "Identification",
    patientRecordLabel: "Patient record number",
    patientRecordPlaceholder: "e.g. 2025-00123",
    anamnesis: "History",
    ageLabel: "Age",
    ageSuffix: "years",
    nihssHint: "0-42",
    mrsLabel: "pre-mRS",
    mrsHint: "0 independent - 5 severe disability",
    ltswLabel: "LTSW",
    ltswSuffix: "hours",
    ltswHint: "Hours since last seen well",
    wakeupTitle: "Wake-up stroke",
    wakeupLabel: "Wake-up stroke",
    wakeupDesc: "WeTrust: allow LTSW up to 12h if symptoms identified within 6h.",
    wakeupSymptomsLabel: "Symptoms identified within 6h of awakening",
    contextTitle: "Operational context",
    angioLabel: "Philips angiograph available?",
    doacLabel: "DOAC (NOAC) in the previous 48h?",
    aceiLabel: "ACE inhibitor (IECA) in therapy?",
    rangeAge: "Age between 0 and 120",
    rangeNihss: "NIHSS between 0 and 42",
    rangeMrs: "pre-mRS between 0 and 5",
    rangeLtsw: "LTSW in hours (0-168)",
  },
  preResult: {
    title: "Pre-Imaging result",
    weTrustEligible: "Patient meets WeTrust criteria. Randomization can proceed.",
    weTrustNotEligible: "Patient does not meet all WeTrust criteria. Proceed with post-imaging evaluation.",
    doacAlertTitle: "Patient on DOAC (NOAC)",
    doacAlertDesc: "Perform chest CTA and drug levels.",
    randomize: "Randomize",
    continue: "Continue",
    weTrustEligibleAnnounce: "WeTrust eligible",
    weTrustNotEligibleAnnounce: "WeTrust not eligible",
  },
  postImaging: {
    title: "Post-Imaging",
    subtitle: "Stroke type, candidacy and parameters.",
    strokeTypeStep: "Stroke type",
    detailsStep: "Details",
    confirmStep: "Confirm",
    strokeTypeLabel: "Stroke type",
    ischemic: "Ischemic",
    ischemicDesc: "LVO or non-LVO",
    hemorrhagic: "Hemorrhagic",
    hemorrhagicDesc: "Spontaneous ICH",
    candidacyTitle: "Treatment candidacy",
    candidateEvt: "Candidate for thrombectomy (EVT)?",
    candidateIvt: "Candidate for thrombolysis (IVT)?",
    candidateEligible: "Eligible",
    candidateNotEligible: "Not eligible",
    evtTitle: "EVT imaging",
    evtDesc: "Required for ATHENA, PIVOTAL, MOSTE and other EVT trials.",
    tandemLabel: "Tandem occlusion?",
    tortuosityLabel: "Vessel tortuosity?",
    tortuosityHint: "If unspecified, some trials will be 'Not indicated'.",
    vesselsLabel: "Target vessel",
    vesselsHint: "Select one or more occluded vessels.",
    contraTpaLabel: "Contraindications to thrombolysis?",
    aspectsLabel: "ASPECTS",
    aspectsHint: "0-10",
    nonEvtTitle: "Non-EVT ischemic confirmation",
    lesionConfirmedLabel: "Imaging lesion confirmed (CT / CTP / MRI)?",
    hemTitle: "Hemorrhagic parameters",
    hemVolumeLabel: "Hemorrhage volume",
    hemVolumeSuffix: "mL",
    gcsLabel: "GCS",
    gcsHint: "3-15",
    ivhLabel: "Is hemorrhage isolated to IVH?",
    secondaryCauseLabel: "Secondary cause",
    secondaryCauseNone: "None",
    seizureLabel: "Seizures / thrombosis / hypersensitivity to TXA?",
    anticoagLabel: "Anticoagulant therapy",
    anticoagNone: "None",
    fastestTitle: "FASTEST - Additional questions",
    fastestHint: "Shown only when LTSW < 2h and pre-mRS <= 2.",
    brainstemLabel: "Brainstem hemorrhage?",
    procoagulantLabel: "Procoagulant drugs in last 24h?",
    ecgLabel: "ECG abnormalities?",
    thrombosisLabel: "History of acute thrombosis?",
    pregnancyLabel: "Pregnancy?",
    angioplastyLabel: "Recent angioplasty?",
    ivhScoreLabel: "Known IVH score?",
  },
  summary: {
    title: "Eligible trials",
    subtitle: "Select one or more trials to proceed.",
    noneTitle: "No eligible trial",
    noneDesc: "The patient does not meet criteria for any active trial.",
    proceed: "Proceed to save",
    goToPostAcute: "Evaluate post-acute (Librexia)",
    selectedCount: "selected",
  },
  share: {
    title: "Save to Google Sheet",
    subtitle: "Review and submit patient record.",
    outcomeFor: "Outcome for",
    intervention: "Intervention",
    control: "Control",
    notesLabel: "Notes",
    notesPlaceholder: "Free notes...",
    messagePreview: "Message preview",
    copy: "Copy",
    sendToSheet: "Send to Sheet",
    saveSuccess: "Saved successfully",
    saveError: "Save error",
  },
  postAcute: {
    title: "Post-acute (Librexia)",
    subtitle: "Evaluation for post-acute trial.",
    causeSuspectLabel: "Suspected atherothrombotic cause?",
    persistentLabel: "Persistent symptoms?",
    imagingFindingLabel: "Imaging consistent with atherothrombotic etiology?",
    treatmentReceivedLabel: "Treatment received in acute phase?",
    abcd2Label: "ABCD2 score",
    abcd2Hint: "0-7",
    labsTitle: "Lab values",
    inrLabel: "INR",
    apttLabel: "aPTT ratio",
    gfrLabel: "GFR",
    chronicAnticoagLabel: "Chronic anticoagulation ongoing?",
    hepaticLabel: "Hepatic disease?",
    pregnancyLabel: "Pregnancy?",
    eligibleTitle: "Librexia eligible",
    notEligibleTitle: "Librexia not eligible",
  },
  trialPatient: {
    title: "Patient from trial",
    subtitle: "Quick entry for an already decided enrollment.",
    selectedTrialLabel: "Selected trial",
    armLabel: "Arm",
    otherTrialsLabel: "Other associated trials",
    addTrial: "Add trial",
    targetVesselLabel: "Target vessel",
    acuteTreatmentsLabel: "Acute treatments",
    tevLabel: "Thrombectomy (TEV)?",
    mticiLabel: "mTICI",
    tivLabel: "Thrombolysis (IVT)?",
  },
  extras: {
    show: "Show",
    hide: "Hide",
    review: "Review",
    continueWithoutSelection: "Continue without selection",
    criteriaMissing: "Missing criteria",
    proceedHow: "How would you like to proceed?",
    sinceLtsw: "since LTSW",
    activeSession: "Active session",
    severity: "Severity",
    patternCause: "Pattern and cause",
    eligFactors: "Eligibility factors",
    isolatedIvh: "Isolated IVH",
    isolatedIvhSub: "Bleeding confined to the ventricular system",
    txaExcl: "TXA exclusion",
    txaExclSub: "Seizures / thrombosis / TXA hypersensitivity",
    acuteTreatments: "Acute treatments",
    noEligibleTitle: "No eligible trial",
    noEligibleDesc: "The patient does not meet criteria for any active trial.",
    windowIv: "IV thrombolysis window",
    windowEvt: "Standard EVT window",
    windowEvtSelected: "Selected EVT window (<=24h)",
    windowClosed: "Window closed",
    symptomsWithin6h: "Symptoms within 6h",
    eligibleTrialsCount: "eligible trials",
    results: "Results",
    selectDateTime: "Select date & time",
    setTime: "Set",
    nowL: "Now",
    tabPatients: "Patients",
    tabStats: "Statistics",
    statScreened: "Screened",
    statEnrolled: "Enrolled",
    statRate: "Rate",
    statIschemic: "Ischemic",
    statHemorrhagic: "Hemorrhagic",
    statEnrollmentsByTrial: "Enrollments by trial",
    statNoData: "No data yet.",
    savedEmpty: "No saved patient yet.",
    savedSubtitle: "Local history - tap to reopen",
    savedRemoveConfirm: "Remove",
    senza: "(no ID)",
    age: "Age",
    volumeTitle: "Hemorrhage volume",
    notAvailable: "Not available",
    volPending: "Volume pending",
    largeHematoma: "Large hematoma (>=30 mL)",
    dragVolume: "Drag or tap on the circle",
    gcsSevere: "Severe",
    gcsModerate: "Moderate",
    gcsMild: "Mild",
    coreLarge: "Large core",
    coreModerate: "Moderate core",
    coreFavorable: "Favorable core",
    vesselsNone: "No vessel selected",
    ichScore: "ICH Score",
    mortality30: "30d mortality:",
    volPendingScore: "Volume pending - partial score",
    catalog: "Catalog",
    fastTag: "Fast",
    guidedTag: "Guided",
    directEnroll: "Direct enrollment",
    directEnrollDesc: "Fast entry for an already decided enrollment",
    skipToContent: "Skip to content",
    causeNone: "None",
    causeTrauma: "Trauma",
    causeAVM: "AVM",
    causeAneurysm: "Aneurysm",
    causeTumor: "Tumor",
    causeOther: "Other",
    anticoagHep: "Heparin <24h",
    regionPosterior: "Posterior",
    quickSubtitle: "Fast entry without eligibility evaluation.",
    quickTrialLabel: "Assigned trial",
    quickTrialPlaceholder: "Select a trial...",
    quickArmLabel: "Arm",
    quickIntervention: "Intervention",
    quickControl: "Control",
    sendDirect: "Send directly",
    sentSuccess: "Sent",
    goHome: "Go to home",
    trialsEnrolled: "Enrolled trials",
    trialsEligibleNotEnrolled: "Eligible not enrolled",
    edit: "Edit",
    deleteAction: "Delete",
    notFound: "Not found",
    notInLocalArchive: "Patient not in local archive",
    syncToFetch: "Sync the list to download the patient from the Sheet.",
    missedShort: "missed",
    cloudPill: "cloud",
  },
};
